import { useEffect, useState, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Icon, IconMehr, type IconName } from './Icon';
import s from './knoepfe.module.css';

const cx = (...k: Array<string | false | undefined>) => k.filter(Boolean).join(' ');

type KnopfArt = 'primaer' | 'kontur' | 'text';

interface KnopfBasis {
  art?: KnopfArt;
  gross?: boolean;
  breit?: boolean;
  icon?: IconName;
  iconRechts?: IconName;
  children: ReactNode;
}

function inhalt({ icon, iconRechts, gross, children }: KnopfBasis) {
  const g = gross ? 22 : 20;
  return (
    <>
      {icon && <Icon name={icon} groesse={g} />}
      {children}
      {iconRechts && <Icon name={iconRechts} groesse={18} strich={2} />}
    </>
  );
}

function klassen({ art = 'primaer', gross, breit }: KnopfBasis, extra?: string) {
  return cx(s.knopf, s[art], gross && art === 'primaer' && s.gross, breit && s.breit, extra);
}

/** Primaer-, Kontur- oder Textknopf (GL-023 §9.1, §9.2). */
export function Knopf(props: KnopfBasis & Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'>) {
  const { art, gross, breit, icon, iconRechts, children, className, type = 'button', ...rest } = props;
  return (
    <button type={type} className={klassen({ art, gross, breit, children }, className)} {...rest}>
      {inhalt({ icon, iconRechts, gross, children })}
    </button>
  );
}

/** Wie Knopf, aber als Link (Navigation statt Aktion). */
export function KnopfLink(props: KnopfBasis & { an: string }) {
  const { an, ...basis } = props;
  return (
    <Link to={an} className={klassen(basis)}>
      {inhalt(basis)}
    </Link>
  );
}

type IconKnopfArt = 'kopf' | 'inhalt' | 'inhalt48' | 'foto' | 'menue';

interface IconKnopfProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children' | 'aria-label'> {
  /** Pflicht: beschreibt die Aktion, das Icon allein reicht nie (GL-023 §9.3). */
  label: string;
  icon: IconName | 'mehr';
  art?: IconKnopfArt;
  groesse?: 18 | 20 | 22;
}

/** Runder Icon-Knopf, 44 px. Kopfzeile: art="kopf". */
export function IconKnopf({ label, icon, art = 'kopf', groesse = 22, className, type = 'button', ...rest }: IconKnopfProps) {
  const strich = icon === 'plus' ? 2.2 : icon.startsWith('chevron') ? 2 : 1.8;
  return (
    <button
      type={type}
      aria-label={label}
      className={cx(s.iconKnopf, art === 'inhalt48' ? cx(s.inhalt, s.inhalt48) : s[art], className)}
      {...rest}
    >
      {icon === 'mehr' ? <IconMehr /> : <Icon name={icon} groesse={groesse} strich={strich} />}
    </button>
  );
}

/** Profilknopf mit Initiale (Kopf der Startseite). */
export function ProfilKnopf({ name, onClick }: { name: string; onClick?: () => void }) {
  return (
    <button type="button" aria-label={`Profil von ${name}`} className={cx(s.iconKnopf, s.profil)} onClick={onClick}>
      {name.slice(0, 1).toUpperCase()}
    </button>
  );
}

/** Filter-Chip, umschaltbar (GL-023 §9.4). Steht immer in einer Wischleiste. */
export function Chip({
  aktiv,
  onClick,
  children,
}: {
  aktiv: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button type="button" className={s.chip} aria-pressed={aktiv} onClick={onClick}>
      {children}
    </button>
  );
}

/** Schwebender Nora-Knopf (GL-023 §9.7), immer gefuellt. */
/**
 * Schwebender Nora-Knopf (GL-023 §9.7). QA Runde 2 N13: beim Runterscrollen gleitet er weg, damit er keine Mengen
 * oder Knoepfe verdeckt; beim Hochscrollen und mit Tastaturfokus ist er sofort wieder da.
 */
export function NoraKnopf({ an = '/nora', label = 'Nora fragen' }: { an?: string; label?: string }) {
  const [weg, setWeg] = useState(false);
  useEffect(() => {
    let zuletzt = window.scrollY;
    const scroll = () => {
      const y = window.scrollY;
      if (y > zuletzt + 8 && y > 40) setWeg(true);
      else if (y < zuletzt - 8 || y <= 40) setWeg(false);
      if (Math.abs(y - zuletzt) > 8) zuletzt = y;
    };
    window.addEventListener('scroll', scroll, { passive: true });
    return () => window.removeEventListener('scroll', scroll);
  }, []);
  return (
    <Link to={an} className={`${s.nora} ${weg ? s.noraWeg : ''}`} aria-label={label} onFocus={() => setWeg(false)}>
      <Icon name="nora" groesse={22} />
      <span className={s.noraWort} aria-hidden="true">
        Nora
      </span>
    </Link>
  );
}
